import { z } from "zod";

/**
 * The single reader of import.meta.env. Validates at module load so a
 * misconfigured build fails at boot, loudly, instead of at first request
 * with an undefined baseURL. ESLint bans import.meta.env everywhere else.
 *
 * Optionality mirrors the committed .env.* files and the CI pipeline:
 * - VITE_WORKOS_CLIENT_ID is absent from .env.demo (the pipeline injects it
 *   as a build-time env var), so it is optional here.
 * - VITE_AI_API_BASE_URL is absent from every committed .env file; callers
 *   handle undefined.
 */
const EnvSchema = z.object({
  VITE_WORKOS_CLIENT_ID: z.string().min(1).optional(),
  // Tolerates empty-string injection like the old `|| "api.workos.com"` did.
  VITE_WORKOS_API_HOSTNAME: z
    .string()
    .optional()
    .transform((v) => (v && v.trim() ? v.trim() : "api.workos.com")),
  VITE_WORKOS_DEV_MODE: z
    .string()
    .optional()
    .transform((v) => v === "true"),

  VITE_AGENT_API_BASE_URL: z.url(),
  VITE_PROFILE_API_DOMAIN: z.url(),
  VITE_BODYCAM_API_DOMAIN: z.url(),
  VITE_JOBS_API_BASE_URL: z.url(),
  VITE_NEWS_API_BASE_URL: z.url(),
  VITE_UPLOAD_API_BASE_URL: z.url(),
  VITE_RMS_API_BASE_URL: z.url(),
  VITE_AI_API_BASE_URL: z.url().optional(),

  VITE_WEBSOCKETS_DOMAIN: z.string().startsWith("wss://"),
  // Invalid values fall back to 40 (the old Number(...)-with-fallback behavior).
  VITE_MAX_VIDEOS_PER_REPORT: z.coerce.number().int().positive().default(40).catch(40),
});

const parsed = EnvSchema.safeParse(import.meta.env);
if (!parsed.success) {
  console.error("[env] invalid VITE_ configuration:", z.treeifyError(parsed.error));
  // This throw happens at module scope, before React mounts — no error
  // boundary can catch it. Paint a readable failure into #root instead of
  // shipping a blank page on deploy misconfiguration.
  const root = document.getElementById("root");
  if (root) {
    root.innerHTML =
      '<div style="padding:32px;font-family:system-ui,sans-serif;text-align:center">' +
      "<h2>Configuration error</h2>" +
      "<p>This deployment is missing or has invalid environment configuration. " +
      "See the browser console for details.</p></div>";
  }
  throw new Error("Invalid environment configuration; see console for details.");
}

export const env = Object.freeze({
  workos: {
    /** May be undefined in local demo-mode builds; the pipeline injects it in CI. */
    clientId: parsed.data.VITE_WORKOS_CLIENT_ID,
    apiHostname: parsed.data.VITE_WORKOS_API_HOSTNAME,
    devMode: parsed.data.VITE_WORKOS_DEV_MODE,
  },
  api: {
    agent: parsed.data.VITE_AGENT_API_BASE_URL,
    profile: parsed.data.VITE_PROFILE_API_DOMAIN,
    bodycam: parsed.data.VITE_BODYCAM_API_DOMAIN,
    jobs: parsed.data.VITE_JOBS_API_BASE_URL,
    news: parsed.data.VITE_NEWS_API_BASE_URL,
    upload: parsed.data.VITE_UPLOAD_API_BASE_URL,
    rms: parsed.data.VITE_RMS_API_BASE_URL,
    /** Not configured in any committed .env file; callers must handle undefined. */
    ai: parsed.data.VITE_AI_API_BASE_URL,
  },
  websocketsDomain: parsed.data.VITE_WEBSOCKETS_DOMAIN,
  maxVideosPerReport: parsed.data.VITE_MAX_VIDEOS_PER_REPORT,
} as const);
